import { create } from 'zustand';
import type { GuideLine, RulerUnit } from '../types';

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 5.0;
const ZOOM_LEVELS = [0.1, 0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 3.0, 4.0, 5.0];
const DEFAULT_GRID_SIZE = 10;
const SNAP_THRESHOLD = 5;

interface SnapGuide {
  orientation: 'horizontal' | 'vertical';
  position: number;
  type: 'edge' | 'center' | 'grid';
  source?: 'artboard' | 'element' | 'grid' | 'user-guide';
}

interface ViewportState {
  zoom: number;
  panX: number;
  panY: number;
  isPanning: boolean;
  snapEnabled: boolean;
  smartGuidesEnabled: boolean;
  gridVisible: boolean;
  gridSize: number;
  activeGuides: SnapGuide[];

  guides: GuideLine[];
  showGuides: boolean;
  snapToGuides: boolean;
  guideLockAll: boolean;
  rulerUnit: RulerUnit;

  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomReset: () => void;
  setPan: (x: number, y: number) => void;
  setIsPanning: (val: boolean) => void;
  toggleSnap: () => void;
  setSnapEnabled: (val: boolean) => void;
  toggleSmartGuides: () => void;
  toggleGrid: () => void;
  setGridVisible: (val: boolean) => void;
  setGridSize: (size: number) => void;
  setActiveGuides: (guides: SnapGuide[]) => void;
  clearGuides: () => void;
  fitToScreen: (artboardW: number, artboardH: number, containerW: number, containerH: number) => void;
  centerAtZoom: (artboardW: number, artboardH: number, containerW: number, containerH: number, targetZoom: number) => void;
  homeToggle: (artboardW: number, artboardH: number, containerW: number, containerH: number) => void;

  addGuide: (orientation: GuideLine['orientation'], position: number) => string;
  updateGuide: (id: string, updates: Partial<Pick<GuideLine, 'position' | 'locked' | 'color'>>) => void;
  removeGuide: (id: string) => void;
  clearAllGuides: () => void;
  toggleGuides: () => void;
  toggleSnapToGuides: () => void;
  toggleLockAllGuides: () => void;
  setRulerUnit: (unit: RulerUnit) => void;
}

export const useViewportStore = create<ViewportState>((set, get) => ({
  zoom: 1.0,
  panX: 0,
  panY: 0,
  isPanning: false,
  snapEnabled: true,
  smartGuidesEnabled: true,
  gridVisible: false,
  gridSize: DEFAULT_GRID_SIZE,
  activeGuides: [],

  guides: [],
  showGuides: true,
  snapToGuides: true,
  guideLockAll: false,
  rulerUnit: 'px' as RulerUnit,

  setZoom: (zoom) => {
    set({ zoom: Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom)) });
  },

  zoomIn: () => {
    const { zoom } = get();
    const next = ZOOM_LEVELS.find((l) => l > zoom + 0.001);
    set({ zoom: next ?? ZOOM_MAX });
  },

  zoomOut: () => {
    const { zoom } = get();
    const prev = [...ZOOM_LEVELS].reverse().find((l) => l < zoom - 0.001);
    set({ zoom: prev ?? ZOOM_MIN });
  },

  zoomReset: () => {
    set({ zoom: 1.0, panX: 0, panY: 0 });
  },

  setPan: (x, y) => set({ panX: x, panY: y }),
  setIsPanning: (val) => set({ isPanning: val }),

  toggleSnap: () => set((s) => ({ snapEnabled: !s.snapEnabled })),
  setSnapEnabled: (val) => set({ snapEnabled: val }),
  toggleSmartGuides: () => set((s) => ({ smartGuidesEnabled: !s.smartGuidesEnabled })),
  toggleGrid: () => set((s) => ({ gridVisible: !s.gridVisible })),
  setGridVisible: (val) => set({ gridVisible: val }),
  setGridSize: (size) => set({ gridSize: Math.max(1, Math.min(200, size)) }),

  setActiveGuides: (guides) => set({ activeGuides: guides }),
  clearGuides: () => set({ activeGuides: [] }),

  fitToScreen: (artboardW, artboardH, containerW, containerH) => {
    const padding = 60;
    const availW = containerW - padding * 2;
    const availH = containerH - padding * 2;
    const scaleX = availW / artboardW;
    const scaleY = availH / artboardH;
    const zoom = Math.min(scaleX, scaleY, ZOOM_MAX);
    const panX = (containerW - artboardW * zoom) / 2;
    const panY = (containerH - artboardH * zoom) / 2;
    set({ zoom, panX, panY });
  },

  centerAtZoom: (artboardW, artboardH, containerW, containerH, targetZoom) => {
    const zoom = Math.min(Math.max(targetZoom, ZOOM_MIN), ZOOM_MAX);
    const panX = (containerW - artboardW * zoom) / 2;
    const panY = (containerH - artboardH * zoom) / 2;
    set({ zoom, panX, panY });
  },

  homeToggle: (artboardW, artboardH, containerW, containerH) => {
    const padding = 60;
    const availW = containerW - padding * 2;
    const availH = containerH - padding * 2;
    const fitZoom = Math.min(availW / artboardW, availH / artboardH, ZOOM_MAX);
    const { zoom } = get();
    const atFit = Math.abs(zoom - fitZoom) < 0.005;
    const targetZoom = atFit ? Math.min(1.0, ZOOM_MAX) : fitZoom;
    const panX = (containerW - artboardW * targetZoom) / 2;
    const panY = (containerH - artboardH * targetZoom) / 2;
    set({ zoom: targetZoom, panX, panY });
  },

  addGuide: (orientation, position) => {
    const id = `guide-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const guide: GuideLine = {
      id,
      orientation,
      position,
      locked: get().guideLockAll,
      color: orientation === 'horizontal' ? '#22d3ee' : '#a78bfa',
    };
    set({ guides: [...get().guides, guide] });
    return id;
  },

  updateGuide: (id, updates) => {
    set({
      guides: get().guides.map((g) => (g.id === id ? { ...g, ...updates } : g)),
    });
  },

  removeGuide: (id) => {
    set({ guides: get().guides.filter((g) => g.id !== id) });
  },

  clearAllGuides: () => {
    set({ guides: [] });
  },

  toggleGuides: () => {
    set({ showGuides: !get().showGuides });
  },

  toggleSnapToGuides: () => {
    set({ snapToGuides: !get().snapToGuides });
  },

  toggleLockAllGuides: () => {
    const newLock = !get().guideLockAll;
    set({
      guideLockAll: newLock,
      guides: get().guides.map((g) => ({ ...g, locked: newLock })),
    });
  },

  setRulerUnit: (unit) => {
    set({ rulerUnit: unit });
  },
}));

export { ZOOM_MIN, ZOOM_MAX, ZOOM_LEVELS, SNAP_THRESHOLD, DEFAULT_GRID_SIZE };
export type { SnapGuide };
